import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { detectOccasion, occasionById } from "./engine";

const OccasionCtx = createContext({ occasion: null, enabled: false });
export const useOccasion = () => useContext(OccasionCtx);

const API = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_KEY = "sav_occasion_preview";     // set ONLY from the authenticated Admin panel
const ADMIN_TOKEN_KEY = "sav_admin_token";

// Isolated provider. Provides occasion context to the app (e.g. Smsma greeting)
// and drives the decorative layer via <OccasionMount/>. When the engine is OFF
// with no admin preview / force => occasion is null => nothing renders => the
// site is byte-identical.
//
// SECURITY: a public visitor can NEVER trigger a preview. Preview is honoured
// only after the admin JWT (sav_admin_token) is verified server-side via
// /api/admin/me. Any query param or localStorage value is ignored otherwise.
export function OccasionProvider({ children }) {
  const [state, setState] = useState({ enabled: false, uae: null, force: null, overrides: {} });
  const [isAdmin, setIsAdmin] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchState = () => fetch(`${API}/api/occasion/state`).then((r) => r.json()).then(setState).catch(() => {});

  useEffect(() => {
    fetchState();
    // light polling so engine toggles / force apply+cancel / auto-stop reach
    // already-open visitor tabs without a manual reload.
    const iv = setInterval(fetchState, 60000);
    const onVis = () => { if (document.visibilityState === "visible") fetchState(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Verify admin session server-side before allowing any preview capability.
  useEffect(() => {
    let alive = true;
    const token = (() => { try { return localStorage.getItem(ADMIN_TOKEN_KEY); } catch { return null; } })();
    if (!token) return;
    fetch(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (r.ok && alive) setIsAdmin(true); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!isAdmin) { setPreview(null); return; }
    try {
      const q = new URLSearchParams(window.location.search).get("occasion");
      if (q) localStorage.setItem(PREVIEW_KEY, q);
      setPreview(localStorage.getItem(PREVIEW_KEY) || null);
    } catch { setPreview(null); }
  }, [isAdmin]);

  const uaeDate = state?.uae?.date;
  let occasion = null;
  if (uaeDate) {
    if (isAdmin && preview) {
      const o = occasionById(preview);
      if (o) occasion = { ...o, dateSource: "preview", preview: true };
    } else if (state.force && state.force.occasion_id) {
      const o = occasionById(state.force.occasion_id);
      if (o) occasion = { ...o, dateSource: "admin_force", forced: true };
    } else if (state.enabled) {
      occasion = detectOccasion(uaeDate, state.overrides || {});
    }
  }

  const settings = {
    desktop: state.desktop !== false,
    mobile: state.mobile !== false,
    cinematicIntro: state.cinematic_intro !== false || !!occasion?.preview,
    intensity: state.intensity || "full",
  };

  const clearPreview = () => { try { localStorage.removeItem(PREVIEW_KEY); } catch {} setPreview(null); };

  return (
    <OccasionCtx.Provider value={{ ...state, occasion, settings, isAdmin, preview, setPreview, clearPreview }}>
      {children}
    </OccasionCtx.Provider>
  );
}

// Lazy-load the decorative layer only when needed (zero cost when OFF).
const OccasionLayer = React.lazy(() => import("./OccasionLayer"));

// Route-aware mount: renders the decorative layer ONLY on public site routes.
// Never renders on /admin or /vetasphere (kept fully separate per requirements).
export function OccasionMount() {
  const { occasion, settings } = useOccasion();
  const { pathname } = useLocation();
  if (!occasion) return null;
  if (pathname.startsWith("/admin") || pathname.startsWith("/vetasphere")) return null;
  const lang = pathname.startsWith("/ar") ? "ar" : "en";
  return (
    <React.Suspense fallback={null}>
      <OccasionLayer occasion={occasion} settings={settings} lang={lang} />
    </React.Suspense>
  );
}
